import { Component, OnInit, OnDestroy } from '@angular/core';
import { AsyncAdminService } from 'src/app/services/async-admin.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { Subject, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';
import * as alertifyjs from 'alertifyjs';

@Component({
  selector: 'app-async-admin-dashboard',
  templateUrl: './async-admin-dashboard.component.html',
  styleUrls: ['./async-admin-dashboard.component.css']
})
export class AsyncAdminDashboardComponent implements OnInit, OnDestroy {

  // ========================================
  // PROPIEDADES PRINCIPALES
  // ========================================
  
  dashboardData: any = null;
  systemStatus: any = null;
  asyncConfig: any = null;
  bdvConfig: any = null;
  retryAnalysis: any = null;
  performanceMetrics: any = null;
  
  // Indicadores BDV Botón de Pagos
  bdvTransactionIndicators: any = null;
  
  loading = false;
  error: string | null = null;
  
  // ========================================
  // FILTROS
  // ========================================
  
  filters = {
    startDate: '',
    endDate: '',
    account_payment: ''
  };
  
  // ========================================
  // ESTADO DE CONFIGURACIÓN
  // ========================================
  
  configPanelOpen = false;
  configLoading = false;

  // ========================================
  // LIFECYCLE Y SUBSCRIPTIONS
  // ========================================
  
  private destroy$ = new Subject<void>();

  constructor(private asyncAdminService: AsyncAdminService, public _auth: AuthService) {}

  ngOnInit(): void {
    console.log('🚀 [ASYNC-ADMIN-DASHBOARD] Iniciando dashboard...');
    
    // Configurar filtros por defecto (día de hoy)
    this.setupDefaultFilters();
    
    // Suscribirse a datos reactivos
    this.subscribeToData();
    
    // Cargar datos iniciales
    this.loadInitialData();
    
    // Auto-refresh con BDV incluido
    this.setupAutoRefresh();
  }

  ngOnDestroy(): void {
    // Limpiar timeout de filtros
    if (this.filterChangeTimeout) {
      clearTimeout(this.filterChangeTimeout);
    }
    
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ========================================
  // CONFIGURACIÓN INICIAL
  // ========================================

  private setupDefaultFilters(): void {
    const today = new Date();
    
    // Obtener la fecha local en formato YYYY-MM-DD (considerando timezone de Venezuela)
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayLocal = `${year}-${month}-${day}`;
    
    // Configurar para mostrar solo el día de hoy por defecto (control diario)
    this.filters.startDate = todayLocal;
    this.filters.endDate = todayLocal;
    
    console.log('📅 [ASYNC-ADMIN-DASHBOARD] Filtros por defecto configurados para hoy (hora local):', this.filters.startDate);
    console.log('📅 [ASYNC-ADMIN-DASHBOARD] Fecha UTC original era:', today.toISOString().split('T')[0]);
  }

  private subscribeToData(): void {
    // Suscribirse a loading state
    this.asyncAdminService.loading
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.loading = loading;
        // Limpiar error cuando empieza una nueva carga
        if (loading) {
          this.error = null;
        }
      });

    // Suscribirse a dashboard data
    this.asyncAdminService.dashboardData
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.dashboardData = data;
        console.log('📊 [ASYNC-ADMIN-DASHBOARD] Dashboard data actualizado:', data);
      });

    // Suscribirse a system status
    this.asyncAdminService.systemStatus
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.systemStatus = status;
        console.log('⚡ [ASYNC-ADMIN-DASHBOARD] System status actualizado:', status);
      });

    // Suscribirse a indicadores BDV
    this.asyncAdminService.bdvTransactionIndicators
      .pipe(takeUntil(this.destroy$))
      .subscribe(indicators => {
        this.bdvTransactionIndicators = indicators;
        console.log('🏦 [ASYNC-ADMIN-DASHBOARD] Indicadores BDV actualizados:', indicators);
      });
  }

  private async loadInitialData(): Promise<void> {
    try {
      console.log('📡 [ASYNC-ADMIN-DASHBOARD] Cargando datos iniciales...');
      
      await Promise.all([
        this.loadAsyncConfig(),
        this.loadBDVConfig(),
        this.loadDashboardOverview(),
        // this.loadRetryAnalysis(), // OCULTO: No necesario por ahora
        this.loadPerformanceMetrics(),
        this.loadBDVTransactionIndicators()
      ]);
      
      console.log('✅ [ASYNC-ADMIN-DASHBOARD] Datos iniciales cargados exitosamente');
      
    } catch (error) {
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error cargando datos iniciales:', error);
      this.error = 'Error cargando los datos del dashboard';
      alertifyjs.error('Error cargando el dashboard');
    }
  }

  private setupAutoRefresh(): void {
    setInterval(() => {
      if (!this.configLoading) {
        console.log('🔄 [ASYNC-ADMIN-DASHBOARD] Auto-refresh (incluye BDV)...');
        this.refreshSystemStatus();
      }
    }, 60000); // 1 minuto
  }

  // ========================================
  // CARGA DE DATOS
  // ========================================

  async loadAsyncConfig(): Promise<void> {
    try {
      const response = await this.asyncAdminService.getAsyncConfig();
      this.asyncConfig = response.config;
      console.log('⚙️ [ASYNC-ADMIN-DASHBOARD] Configuración async cargada:', this.asyncConfig);
    } catch (error) {
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error cargando configuración:', error);
      throw error;
    }
  }

  async loadBDVConfig(): Promise<void> {
    try {
      const response = await this.asyncAdminService.getBDVConfiguration();
      this.bdvConfig = response.data;
      console.log('🏦 [ASYNC-ADMIN-DASHBOARD] Configuración BDV cargada:', this.bdvConfig);
    } catch (error) {
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error cargando configuración BDV:', error);
      // No lanzar error para que no falle toda la carga
      this.bdvConfig = { auto_conciliation: true }; // Valor por defecto
    }
  }

  async loadDashboardOverview(): Promise<void> {
    try {
      await this.asyncAdminService.getDashboardOverview(this.filters);
    } catch (error) {
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error cargando overview:', error);
      throw error;
    }
  }

  async loadRetryAnalysis(): Promise<void> {
    try {
      const response = await this.asyncAdminService.getRetryAnalysis(this.filters);
      console.log('🔍 [ASYNC-ADMIN-DASHBOARD] Respuesta del análisis de reintentos:', response);
      this.retryAnalysis = response.data;
      console.log('📊 [ASYNC-ADMIN-DASHBOARD] Datos de análisis asignados:', this.retryAnalysis);
    } catch (error) {
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error cargando análisis de reintentos:', error);
      throw error;
    }
  }

  async loadPerformanceMetrics(): Promise<void> {
    try {
      const response = await this.asyncAdminService.getPerformanceMetrics(this.filters);
      this.performanceMetrics = response.data;
    } catch (error) {
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error cargando métricas de performance:', error);
      throw error;
    }
  }

  async loadBDVTransactionIndicators(): Promise<void> {
    try {
      console.log('🏦 [ASYNC-ADMIN-DASHBOARD] Cargando indicadores BDV...');
      await this.asyncAdminService.getBDVTransactionIndicators(this.filters);
      // Los datos se actualizan automáticamente via BehaviorSubject
    } catch (error) {
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error cargando indicadores BDV:', error);
      // No lanzar error para que no falle toda la carga
    }
  }

  async refreshSystemStatus(): Promise<void> {
    try {
      // Refresh completo que incluye BDV
      await Promise.all([
        this.asyncAdminService.getSystemStatus(),
        this.asyncAdminService.getBDVTransactionIndicators(this.filters)
      ]);
    } catch (error) {
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error refrescando estado del sistema:', error);
    }
  }

  // ========================================
  // MANEJO DE FILTROS
  // ========================================

  // Debounce timer para filtros automáticos
  private filterChangeTimeout: any = null;

  /**
   * 📅 Manejo automático de cambio de filtros de fecha
   */
  onDateFilterChange(): void {
    // Cancelar timeout anterior si existe
    if (this.filterChangeTimeout) {
      clearTimeout(this.filterChangeTimeout);
    }

    // Aplicar filtros después de un breve delay (evitar múltiples llamadas)
    this.filterChangeTimeout = setTimeout(() => {
      if (this.filters.startDate && this.filters.endDate) {
        console.log('📅 [ASYNC-ADMIN-DASHBOARD] Auto-aplicando filtros de fecha:', this.filters);
        this.onFiltersChange();
      }
    }, 500); // 500ms de delay
  }

  async onFiltersChange(): Promise<void> {
    try {
      console.log('🔍 [ASYNC-ADMIN-DASHBOARD] Aplicando filtros:', this.filters);
      
      await Promise.all([
        this.loadDashboardOverview(),
        // this.loadRetryAnalysis(), // OCULTO: No necesario por ahora
        this.loadPerformanceMetrics(),
        this.loadBDVTransactionIndicators()
      ]);
      
      alertifyjs.success('Filtros aplicados exitosamente');
      
    } catch (error) {
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error aplicando filtros:', error);
      alertifyjs.error('Error aplicando filtros');
    }
  }

  clearFilters(): void {
    this.filters = {
      startDate: '',
      endDate: '',
      account_payment: ''
    };
    
    this.setupDefaultFilters();
    this.onFiltersChange();
  }

  // ========================================
  // CONFIGURACIÓN ASYNC
  // ========================================

  toggleConfigPanel(): void {
    this.configPanelOpen = !this.configPanelOpen;
  }

  async toggleAsyncFeature(feature: string, currentValue: boolean): Promise<void> {
    try {
      const featureName = feature === 'async' ? 'Verificación Asíncrona' : 'Fallback Manual';
      const action = !currentValue ? 'habilitar' : 'deshabilitar';
      
      const result = await Swal.fire({
        title: `¿${action} ${featureName}?`,
        text: `Esto ${action}á la funcionalidad de ${featureName.toLowerCase()}`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: `Sí, ${action}`,
        cancelButtonText: 'Cancelar'
      });

      if (result.isConfirmed) {
        this.configLoading = true;
        
        const response = await this.asyncAdminService.toggleAsyncFeature(feature, !currentValue);
        
        if (response.success) {
          // Actualizar configuración local
          if (feature === 'async') {
            this.asyncConfig.async_validation_active = !currentValue;
          } else if (feature === 'manual-fallback') {
            this.asyncConfig.auto_manual_fallback_active = !currentValue;
          }
          
          alertifyjs.success(response.message);
          
          // Refrescar datos del dashboard
          await this.refreshSystemStatus();
        }
        
        this.configLoading = false;
      }
      
    } catch (error) {
      this.configLoading = false;
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error en toggle:', error);
      alertifyjs.error('Error actualizando configuración');
    }
  }

  async toggleBDVAutoConciliation(currentValue: boolean): Promise<void> {
    try {
      const action = !currentValue ? 'habilitar' : 'deshabilitar';
      
      const result = await Swal.fire({
        title: `¿${action} Auto-Verificación BDV?`,
        text: `Esto ${action}á la verificación automática de pagos del Banco de Venezuela`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: `Sí, ${action}`,
        cancelButtonText: 'Cancelar'
      });

      if (result.isConfirmed) {
        this.configLoading = true;
        
        const response = await this.asyncAdminService.toggleBDVAutoConciliation(!currentValue);
        
        if (response.success) {
          // Actualizar configuración local
          this.bdvConfig.auto_conciliation = !currentValue;
          
          alertifyjs.success(response.message || 'Configuración BDV actualizada exitosamente');
          
          // Refrescar configuración BDV
          await this.loadBDVConfig();
        } else {
          alertifyjs.error(response.message || 'Error actualizando configuración BDV');
        }
        
        this.configLoading = false;
      }
      
    } catch (error) {
      this.configLoading = false;
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error en toggle BDV:', error);
      alertifyjs.error('Error actualizando configuración BDV');
    }
  }

  async updateConfiguration(): Promise<void> {
    try {
      this.configLoading = true;
      
      const response = await this.asyncAdminService.updateAsyncConfig(this.asyncConfig);
      
      if (response.success) {
        alertifyjs.success('Configuración actualizada exitosamente');
        
        // Refrescar datos
        await this.refreshSystemStatus();
        
        this.configPanelOpen = false;
      }
      
      this.configLoading = false;
      
    } catch (error) {
      this.configLoading = false;
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error actualizando configuración:', error);
      alertifyjs.error('Error actualizando configuración');
    }
  }

  // ========================================
  // UTILITIES Y HELPERS
  // ========================================

  getStatusColor(status: string): string {
    switch (status?.toUpperCase()) {
      case 'HEALTHY': return 'success';
      case 'CAUTION': return 'warning';
      case 'WARNING': return 'warning';
      case 'CRITICAL': return 'danger';
      default: return 'secondary';
    }
  }

  getStatusIcon(status: string): string {
    switch (status?.toUpperCase()) {
      case 'HEALTHY': return 'fas fa-check-circle';
      case 'CAUTION': return 'fas fa-exclamation-triangle';
      case 'WARNING': return 'fas fa-exclamation-triangle';
      case 'CRITICAL': return 'fas fa-times-circle';
      default: return 'fas fa-question-circle';
    }
  }

  formatNumber(value: number | undefined): string {
    if (value === undefined || value === null) return '0';
    return value.toLocaleString();
  }

  formatPercentage(value: number | undefined): string {
    if (value === undefined || value === null) return '0%';
    return `${Number(value).toFixed(1)}%`;
  }

  formatDuration(ms: number | undefined): string {
    if (!ms) return 'N/A';
    
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  }

  formatDateTime(date: string | Date | undefined): string {
    if (!date) return 'N/A';
    
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  }

  // ========================================
  // ACCIONES ADICIONALES
  // ========================================

  async refreshDashboard(): Promise<void> {
    try {
      console.log('🔄 [ASYNC-ADMIN-DASHBOARD] Refrescando dashboard completo...');
      
      await Promise.all([
        this.asyncAdminService.refreshDashboard(this.filters),
        // this.loadRetryAnalysis(), // OCULTO: No necesario por ahora
        this.loadPerformanceMetrics()
      ]);
      
      alertifyjs.success('Dashboard actualizado');
      
    } catch (error) {
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error refrescando dashboard:', error);
      alertifyjs.error('Error actualizando dashboard');
    }
  }

  async exportData(): Promise<void> {
    try {
      // TODO: Implementar exportación de datos
      alertifyjs.notify('Funcionalidad de exportación en desarrollo');
    } catch (error) {
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error exportando datos:', error);
    }
  }

  // Método auxiliar para obtener las claves de paymentsByStatus
  getStatusKeys(paymentsByStatus: any): string[] {
    return paymentsByStatus ? Object.keys(paymentsByStatus) : [];
  }

  // Método auxiliar para formatear números decimales
  formatDecimal(value: number | undefined, decimals: number = 1): string {
    if (value === undefined || value === null || isNaN(value)) return '0.0';
    return Number(value).toFixed(decimals);
  }

  // Método para formatear moneda en bolívares  
  formatCurrencyBs(value: number | undefined): string {
    if (value === undefined || value === null || isNaN(value)) return 'Bs. 0,00';
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'VES',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value).replace('VES', 'Bs.');
  }

  // Método para calcular porcentaje de eficiencia de cada estado
  calculateStatusPercentage(status: string): string {
    if (!this.dashboardData?.asyncSuccessFailureBreakdown) return '0%';
    
    const breakdown = this.dashboardData.asyncSuccessFailureBreakdown;
    const statusData = breakdown[status];
    
    if (!statusData || !statusData.count) return '0%';
    
    // Calcular el total de pagos async
    const totalAsyncPayments = Object.keys(breakdown).reduce((total, key) => {
      return total + (breakdown[key]?.count || 0);
    }, 0);
    
    if (totalAsyncPayments === 0) return '0%';
    
    const percentage = (statusData.count / totalAsyncPayments) * 100;
    return `${percentage.toFixed(1)}%`;
  }

  // Método para obtener el color del porcentaje según el estado
  getPercentageColor(status: string): string {
    switch (status?.toUpperCase()) {
      case 'SUCCESS': return 'text-success';
      case 'FAILED': return 'text-danger';
      case 'PENDING': return 'text-warning';
      default: return 'text-muted';
    }
  }

}
